import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Package, ShoppingBag, DollarSign, Shield, Trash2, Edit2, CheckCircle, Search, X, Star, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const CITIES = ["Toutes", "Chimay", "Couvin", "Momignies", "Beaumont", "Sivry-Rance", "Froidchapelle"];

const AdminDashboard = () => {
  const { user, loading, roles } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState<"orders" | "listings" | "sellers" | "reviews">("orders");
  const [reviews, setReviews] = useState<any[]>([]);
  const [sellers, setSellers] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [listings, setListings] = useState<any[]>([]);
  const [stats, setStats] = useState({ totalCommissions: 0, totalOrders: 0, activeListings: 0, pendingListings: 0 });
  const [testMode, setTestMode] = useState(false);
  const [testModeLoading, setTestModeLoading] = useState(false);
  // Filters
  const [sellerFilter, setSellerFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("Toutes");

  // Edit dialog
  const [editListing, setEditListing] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", price_per_stere: "", location: "", species: "", status: "" });

  // Delete dialog
  const [deleteListing, setDeleteListing] = useState<any | null>(null);

  const isAdmin = roles.includes("admin");

  useEffect(() => {
    if (!user || !isAdmin) return;
    fetchAll();
    fetchTestMode();
  }, [user, isAdmin]);

  const fetchTestMode = async () => {
    const { data } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", "stripe_test_mode")
      .single();
    const val = data?.value as Record<string, unknown> | null;
    setTestMode(val?.enabled === true);
  };

  const toggleTestMode = async () => {
    setTestModeLoading(true);
    const newValue = !testMode;
    const { error } = await supabase
      .from("site_settings")
      .update({ value: { enabled: newValue } as any, updated_at: new Date().toISOString() })
      .eq("key", "stripe_test_mode");
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      setTestMode(newValue);
      toast({ title: newValue ? "Mode Test activé" : "Mode Live activé", description: newValue ? "Les paiements sont maintenant fictifs." : "Les paiements réels sont actifs." });
    }
    setTestModeLoading(false);
  };

  const fetchAll = async () => {
    const [ordersRes, listingsRes, sellersRes, reviewsRes] = await Promise.all([
      supabase.from("orders").select("*, listings(title), buyer:profiles!orders_buyer_id_fkey(full_name, email), seller:profiles!orders_seller_id_fkey(full_name, email)").order("created_at", { ascending: false }).limit(50),
      supabase.from("listings").select("*, seller:profiles!listings_seller_id_fkey(full_name)").order("created_at", { ascending: false }),
      supabase.from("user_roles").select("user_id, profiles!inner(user_id, full_name, email, admin_rating, location)").eq("role", "seller"),
      supabase.from("reviews").select("*").order("created_at", { ascending: false }).limit(100),
    ]);

    setOrders(ordersRes.data ?? []);
    setListings(listingsRes.data ?? []);
    setSellers((sellersRes.data ?? []).map((s: any) => s.profiles).filter(Boolean));

    // Enrich reviews with profile names
    const allReviews = reviewsRes.data ?? [];
    if (allReviews.length > 0) {
      const userIds = [...new Set(allReviews.flatMap((r: any) => [r.reviewer_id, r.seller_id]))];
      const { data: profiles } = await supabase.from("profiles_public").select("*").in("user_id", userIds);
      const profileMap = Object.fromEntries((profiles ?? []).map((p: any) => [p.user_id, p]));
      setReviews(allReviews.map((r: any) => ({
        ...r,
        reviewer_name: profileMap[r.reviewer_id]?.display_name || "Inconnu",
        seller_name: profileMap[r.seller_id]?.display_name || "Inconnu",
      })));
    } else {
      setReviews([]);
    }

    const totalCommissions = (ordersRes.data ?? [])
      .filter((o: any) => o.status === "paid" || o.status === "completed")
      .reduce((sum: number, o: any) => sum + Number(o.commission_amount), 0);

    const allListings = listingsRes.data ?? [];
    setStats({
      totalCommissions,
      totalOrders: ordersRes.data?.length ?? 0,
      activeListings: allListings.filter((l: any) => l.status === "active").length,
      pendingListings: allListings.filter((l: any) => l.status === "pending").length,
    });
  };

  const updateOrderStatus = async (orderId: string, status: string) => {
    const { error } = await supabase.from("orders").update({ status }).eq("id", orderId);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Statut mis à jour" });
      fetchAll();
    }
  };

  const updateListingStatus = async (listingId: string, status: string, adminVerified?: boolean) => {
    const updateData: any = { status };
    if (adminVerified !== undefined) updateData.admin_verified = adminVerified;
    const { error } = await supabase.from("listings").update(updateData).eq("id", listingId);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: status === "active" ? "Annonce approuvée ✓" : "Statut mis à jour" });
      fetchAll();
    }
  };

  const handleDeleteListing = async () => {
    if (!deleteListing) return;
    const { error } = await supabase.from("listings").delete().eq("id", deleteListing.id);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Annonce supprimée" });
      setDeleteListing(null);
      fetchAll();
    }
  };

  const openEdit = (listing: any) => {
    setEditListing(listing);
    setEditForm({
      title: listing.title,
      description: listing.description,
      price_per_stere: String(listing.price_per_stere),
      location: listing.location,
      species: listing.species,
      status: listing.status,
    });
  };

  const handleSaveEdit = async () => {
    if (!editListing) return;
    const { error } = await supabase.from("listings").update({
      title: editForm.title,
      description: editForm.description,
      price_per_stere: parseFloat(editForm.price_per_stere) || 0,
      location: editForm.location,
      species: editForm.species,
      status: editForm.status,
    }).eq("id", editListing.id);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Annonce modifiée" });
      setEditListing(null);
      fetchAll();
    }
  };

  // Filtered listings
  const filteredListings = listings.filter((l: any) => {
    if (sellerFilter && !(l as any).seller?.full_name?.toLowerCase().includes(sellerFilter.toLowerCase())) return false;
    if (cityFilter !== "Toutes" && !l.location?.toLowerCase().includes(cityFilter.toLowerCase())) return false;
    return true;
  });

  if (loading) return null;
  if (!user || !isAdmin) return <Navigate to="/" replace />;

  const statusLabel = (s: string) => {
    switch (s) {
      case "active": return "Actif";
      case "pending": return "En attente";
      case "rejected": return "Rejeté";
      default: return s;
    }
  };

  const statusVariant = (s: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (s) {
      case "active": return "default";
      case "pending": return "outline";
      case "rejected": return "destructive";
      default: return "secondary";
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container py-8 flex-1">
        <div className="flex items-center gap-3 mb-8 flex-wrap justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-primary" />
            <h1 className="font-display text-3xl font-bold">Administration</h1>
          </div>
          <div className="flex items-center gap-3 bg-card border rounded-lg px-4 py-2">
            <FlaskConical className={`h-4 w-4 ${testMode ? "text-amber-500" : "text-muted-foreground"}`} />
            <span className="text-sm font-medium">Mode Test (Paiements fictifs)</span>
            <Switch checked={testMode} onCheckedChange={toggleTestMode} disabled={testModeLoading} />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: DollarSign, label: "Commissions totales", value: `${stats.totalCommissions}€`, color: "text-primary" },
            { icon: ShoppingBag, label: "Commandes", value: stats.totalOrders, color: "text-amber" },
            { icon: Package, label: "Annonces actives", value: stats.activeListings, color: "text-secondary" },
            { icon: Package, label: "En attente", value: stats.pendingListings, color: "text-destructive" },
          ].map((s) => (
            <div key={s.label} className="bg-card rounded-lg border p-4">
              <s.icon className={`h-5 w-5 ${s.color} mb-2`} />
              <p className="text-2xl font-display font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-muted rounded-lg p-1 w-fit">
          {(["orders", "listings", "sellers", "reviews"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"
              }`}
            >
              {t === "orders" ? "Commandes" : t === "listings" ? `Annonces (${listings.length})` : t === "sellers" ? `Vendeurs (${sellers.length})` : `Avis (${reviews.length})`}
            </button>
          ))}
        </div>

        {/* Orders */}
        {tab === "orders" && (
          <div className="space-y-3">
            {orders.length === 0 ? (
              <p className="text-center py-12 text-muted-foreground">Aucune commande</p>
            ) : (
              orders.map((order) => (
                <div key={order.id} className="bg-card rounded-lg border p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{(order as any).listings?.title || "Annonce"}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Acheteur: {(order as any).buyer?.full_name || (order as any).buyer?.email} · 
                        Vendeur: {(order as any).seller?.full_name || (order as any).seller?.email}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Commission: {order.commission_amount}€ · Total: {order.total_wood_price}€ · 
                        {new Date(order.created_at).toLocaleDateString("fr-BE")}
                      </p>
                      {order.wood_pass_code && (
                        <p className="text-xs font-mono text-primary mt-1">WP-{order.wood_pass_code.slice(0, 8).toUpperCase()}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        order.status === "completed" ? "default" :
                        order.status === "paid" ? "secondary" :
                        order.status === "disputed" ? "destructive" : "outline"
                      }>
                        {order.status === "completed" ? "Terminé" :
                         order.status === "paid" ? "Payé" :
                         order.status === "disputed" ? "Litige" : "En attente"}
                      </Badge>
                      <Select value={order.status} onValueChange={(v) => updateOrderStatus(order.id, v)}>
                        <SelectTrigger className="w-[130px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">En attente</SelectItem>
                          <SelectItem value="paid">Payé</SelectItem>
                          <SelectItem value="completed">Terminé</SelectItem>
                          <SelectItem value="disputed">Litige</SelectItem>
                          <SelectItem value="refunded">Remboursé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Listings */}
        {tab === "listings" && (
          <div>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="flex-1 flex items-center gap-2 bg-card rounded-lg px-3 border">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={sellerFilter}
                  onChange={(e) => setSellerFilter(e.target.value)}
                  placeholder="Filtrer par vendeur..."
                  className="w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
                />
                {sellerFilter && <button onClick={() => setSellerFilter("")}><X className="h-3 w-3 text-muted-foreground" /></button>}
              </div>
              <Select value={cityFilter} onValueChange={setCityFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Ville" />
                </SelectTrigger>
                <SelectContent>
                  {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-muted-foreground mb-3">{filteredListings.length} annonce{filteredListings.length !== 1 ? "s" : ""}</p>

            <div className="space-y-3">
              {filteredListings.map((listing: any) => (
                <div key={listing.id} className="bg-card rounded-lg border p-4">
                  <div className="flex items-start gap-4">
                    {listing.image_urls?.[0] && (
                      <img src={listing.image_urls[0]} alt="" className="w-16 h-16 rounded-md object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm">{listing.title}</p>
                        <Badge variant={statusVariant(listing.status)}>{statusLabel(listing.status)}</Badge>
                        {listing.admin_verified && (
                          <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">
                            <CheckCircle className="h-3 w-3 mr-1" /> Vérifié
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {listing.seller?.full_name || "—"} · {listing.species} · {listing.price_per_stere}€/stère · {listing.location}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(listing.created_at).toLocaleDateString("fr-BE")}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {listing.status === "pending" && (
                        <Button size="sm" variant="default" className="h-8 text-xs gap-1" onClick={() => updateListingStatus(listing.id, "active", true)}>
                          <CheckCircle className="h-3 w-3" /> Approuver
                        </Button>
                      )}
                      <Select value={listing.status} onValueChange={(v) => updateListingStatus(listing.id, v, v === "active" ? true : undefined)}>
                        <SelectTrigger className="w-[110px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">En attente</SelectItem>
                          <SelectItem value="active">Actif</SelectItem>
                          <SelectItem value="rejected">Rejeté</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(listing)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setDeleteListing(listing)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sellers */}
        {tab === "sellers" && (
          <div className="space-y-3">
            {sellers.length === 0 ? (
              <p className="text-center py-12 text-muted-foreground">Aucun vendeur</p>
            ) : (
              sellers.map((s: any) => (
                <div key={s.user_id} className="bg-card rounded-lg border p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                    {(s.full_name || "V").charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{s.full_name || "Sans nom"}</p>
                    <p className="text-xs text-muted-foreground">{s.email} · {s.location || "—"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Note :</span>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={async () => {
                            const newRating = s.admin_rating === star ? 0 : star;
                            const { error } = await supabase
                              .from("profiles")
                              .update({ admin_rating: newRating } as any)
                              .eq("user_id", s.user_id);
                            if (error) {
                              toast({ title: "Erreur", description: error.message, variant: "destructive" });
                            } else {
                              toast({ title: `Note mise à jour : ${newRating}/5` });
                              fetchAll();
                            }
                          }}
                          className="p-0.5 hover:scale-110 transition-transform"
                        >
                          <Star className={`h-5 w-5 ${star <= (s.admin_rating || 0) ? "text-amber-500 fill-amber-500" : "text-muted-foreground/30"}`} />
                        </button>
                      ))}
                    </div>
                    <span className="text-sm font-bold ml-1">{s.admin_rating || 0}/5</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Reviews moderation */}
        {tab === "reviews" && (
          <div className="space-y-3">
            {reviews.length === 0 ? (
              <p className="text-center py-12 text-muted-foreground">Aucun avis</p>
            ) : (
              reviews.map((r: any) => (
                <div key={r.id} className="bg-card rounded-lg border p-4 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`h-3.5 w-3.5 ${s <= r.rating ? "text-amber-500 fill-amber-500" : "text-muted-foreground/30"}`} />
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString("fr-BE")}
                      </span>
                    </div>
                    <p className="text-sm">
                      <span className="font-medium">{r.reviewer_name}</span>
                      <span className="text-muted-foreground"> → </span>
                      <span className="font-medium">{r.seller_name}</span>
                    </p>
                    {r.comment && <p className="text-sm text-muted-foreground mt-1">{r.comment}</p>}
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="shrink-0 gap-1"
                    onClick={async () => {
                      if (!confirm("Supprimer cet avis ?")) return;
                      const { error } = await supabase.from("reviews").delete().eq("id", r.id);
                      if (error) {
                        toast({ title: "Erreur", description: error.message, variant: "destructive" });
                      } else {
                        toast({ title: "Avis supprimé" });
                        fetchAll();
                      }
                    }}
                  >
                    <Trash2 className="h-3 w-3" /> Supprimer
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editListing} onOpenChange={(o) => !o && setEditListing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'annonce</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Titre</label>
              <Input value={editForm.title} onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Description</label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Prix/stère (€)</label>
                <Input type="number" value={editForm.price_per_stere} onChange={(e) => setEditForm(f => ({ ...f, price_per_stere: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Localisation</label>
                <Input value={editForm.location} onChange={(e) => setEditForm(f => ({ ...f, location: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Essence</label>
              <Input value={editForm.species} onChange={(e) => setEditForm(f => ({ ...f, species: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={handleSaveEdit}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteListing} onOpenChange={(o) => !o && setDeleteListing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer cette annonce ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            L'annonce « {deleteListing?.title} » sera supprimée définitivement.
          </p>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button variant="destructive" onClick={handleDeleteListing}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
